import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { naira } from "../../lib/format";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Search, ExternalLink, FileDown, FileText } from "lucide-react";
import { C } from "./AccountingLayout";
import { downloadReport } from "./exportHelpers";

export default function GeneralLedger() {
  const { locationId } = useOutletContext();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ account: "", date_from: "", date_to: "", source: "" });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/accounts").then(setAccounts).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    const p = { ...(locationId ? { location_id: locationId } : {}), ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
    api.get("/reports/general-ledger", { params: p }).then(setRows).catch(() => {}).finally(() => setLoading(false));
  }, [f, locationId]);

  const filtered = useMemo(() => rows.filter((r) =>
    !q || [r.journal_number, r.memo, r.reference, r.account_name].some((x) => (x || "").toLowerCase().includes(q.toLowerCase()))
  ), [rows, q]);

  const doExport = async (fmt) => {
    setBusy(fmt);
    const date = new Date().toISOString().slice(0, 10);
    const p = { ...(locationId ? { location_id: locationId } : {}), ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)) };
    await downloadReport(`/reports/exports/general-ledger.${fmt}`, `general-ledger-${date}.${fmt}`, p);
    setBusy(null);
  };

  return (
    <div className="space-y-4" data-testid="general-ledger-page">
      <div className="rounded-xl bg-white border p-4 flex flex-wrap items-end gap-3" style={{ borderColor: C.border }}>
        <div className="flex-1 min-w-[200px] max-w-md flex items-center gap-2 h-11 px-3 rounded-lg border" style={{ borderColor: C.border, background: "#FDFBF5" }}>
          <Search size={15} style={{ color: C.softMuted }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search journal, account, reference…" className="bg-transparent border-0 outline-none text-sm flex-1" style={{ color: C.text }} data-testid="gl-search" />
        </div>
        <Select value={f.account || "all"} onValueChange={(v) => setF({ ...f, account: v === "all" ? "" : v })}>
          <SelectTrigger className="w-56 h-11" data-testid="gl-account"><SelectValue placeholder="Account" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All accounts</SelectItem>{accounts.filter((a) => a.active !== false).map((a) => <SelectItem key={a.code} value={a.code}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="h-11 w-40" data-testid="gl-from" />
        <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="h-11 w-40" data-testid="gl-to" />
        <Select value={f.source || "all"} onValueChange={(v) => setF({ ...f, source: v === "all" ? "" : v })}>
          <SelectTrigger className="w-36 h-11"><SelectValue placeholder="Source" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All sources</SelectItem>{["pos", "online", "manual", "procurement", "expense", "reversal", "seed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <button onClick={() => doExport("csv")} disabled={busy === "csv"} className="h-11 px-3 text-[12px] font-semibold rounded-md flex items-center gap-1.5 disabled:opacity-50" style={{ background: C.gold, color: "#FFFFFF" }} data-testid="gl-export-csv"><FileDown size={13} /> {busy === "csv" ? "…" : "CSV"}</button>
          <button onClick={() => doExport("pdf")} disabled={busy === "pdf"} className="h-11 px-3 text-[12px] font-semibold rounded-md flex items-center gap-1.5 disabled:opacity-50 text-white" style={{ background: C.burgundy }} data-testid="gl-export-pdf"><FileText size={13} /> {busy === "pdf" ? "…" : "PDF"}</button>
        </div>
      </div>

      <div className="rounded-xl bg-white border overflow-hidden" style={{ borderColor: C.border }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest border-b" style={{ color: C.softMuted, borderColor: C.borderSoft }}>
                <th className="py-3 px-4">Date</th><th className="py-3 px-3">Journal</th><th className="py-3 px-3">Account</th>
                <th className="py-3 px-3">Description</th><th className="py-3 px-3">Source</th>
                <th className="py-3 px-3 text-right">Debit</th><th className="py-3 px-3 text-right">Credit</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="py-8 text-center" style={{ color: C.softMuted }}>Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={8} className="py-8 text-center" style={{ color: C.softMuted }}>No entries in this range.</td></tr>}
              {filtered.map((r, i) => (
                <tr key={i} className="border-b hover:bg-[#FAF6EC]" style={{ borderColor: C.borderSoft }}>
                  <td className="py-2.5 px-4 text-[12px]" style={{ color: C.muted }}>{(r.date || "").slice(0, 10)}</td>
                  <td className="py-2.5 px-3 font-semibold text-[12.5px]" style={{ color: C.burgundy }}>{r.journal_number}</td>
                  <td className="py-2.5 px-3 text-[12.5px]" style={{ color: C.text }}><span className="font-mono text-[11px] mr-1.5" style={{ color: C.softMuted }}>{r.account_code}</span>{r.account_name}</td>
                  <td className="py-2.5 px-3 text-[12.5px]" style={{ color: C.text }}>{r.description || r.memo}</td>
                  <td className="py-2.5 px-3 text-[11px] uppercase font-semibold" style={{ color: C.softMuted }}>{r.source}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums" style={{ color: r.debit > 0 ? C.text : "transparent" }}>{r.debit > 0 ? naira(r.debit) : "—"}</td>
                  <td className="py-2.5 px-3 text-right tabular-nums" style={{ color: r.credit > 0 ? C.text : "transparent" }}>{r.credit > 0 ? naira(r.credit) : "—"}</td>
                  <td className="py-2.5 px-4">
                    <button onClick={() => navigate(`/accounting/journals?jid=${r.journal_id}`)} className="text-[12px] font-semibold flex items-center gap-1" style={{ color: C.gold }} data-testid={`gl-drill-${i}`}>Drill <ExternalLink size={11} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
